/**
 * CoinGecko API Proxy
 * Routes all CoinGecko requests through the backend proxy at /coingecko/*
 * The API key lives server-side only — never exposed to the browser.
 * Backend has its own in-memory cache (30-60s TTL) so load times stay fast.
 */

const BACKEND = process.env.REACT_APP_BACKEND_SERVER || "";

// Minimal client-side rate limiting (backend does the heavy lifting)
let lastRequestTime = 0;
const MIN_DELAY_MS = 500; // lighter delay since backend caches

/**
 * Rewrite a CoinGecko URL to the backend proxy.
 * e.g. https://api.coingecko.com/api/v3/simple/price?ids=bitcoin...
 *   → {BACKEND}coingecko/simple/price?ids=bitcoin...
 */
function rewriteUrl(url) {
  // Extract the path after /api/v3/
  const match = url.match(/coingecko\.com\/api\/v3\/(.+)/);
  if (match) {
    return `${BACKEND}coingecko/${match[1]}`;
  }
  // If already a relative/backend URL, return as-is
  return url;
}

/**
 * Rate-limited fetch wrapper that proxies through the backend.
 * Drop-in replacement — all existing callers keep working unchanged.
 * @param {string} url - Original CoinGecko API URL
 * @param {RequestInit} options - Fetch options (headers are stripped since backend handles auth)
 * @returns {Promise<Response>} Fetch response
 */
export const rateLimitedFetch = async (url, options = {}) => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (timeSinceLastRequest < MIN_DELAY_MS) {
    const waitTime = MIN_DELAY_MS - timeSinceLastRequest;
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }

  lastRequestTime = Date.now();

  const proxyUrl = rewriteUrl(url);

  return fetch(proxyUrl, {
    ...options,
    headers: {
      Accept: "application/json",
    },
  });
};

/**
 * Reset the rate limiter (useful for testing or manual resets)
 */
export const resetRateLimiter = () => {
  lastRequestTime = 0;
};
